import React, { useEffect, useState, useCallback } from 'react';
import Card from "../../common/card/Card";
import BreadcrumbsBar from "../../layouts/breadcrumb/BreadcrumbsBar";
import { getAllLCWUsersStream, updateLCWUserReviewState } from "../../services/lcwUsersService";
import { t } from "i18next";
import GenericTable from "../../common/table/GenericTable";
import dayjs from "dayjs";
import { Button, Chip, LinearProgress, Typography, Box, Stack } from "@mui/material";
import { BlockOutlined, DoneOutlined, LaunchOutlined, LocalDiningOutlined, FilterAlt, Clear } from "@mui/icons-material";
import styles from "./LcwNewPartners.module.scss";
import Toast from "../../common/toast/Toast";
import useToast from "../../common/hooks/useToast";
import { DOWNLOAD_LCW_USERS_CSV } from "../../common/constants/routes";
import { getInstagramLink } from "../../common/utils";
import ExportDropdown from "../../common/exportDropdown/ExportDropdown";
import { EXPORT_FILE_TYPES } from "../AdvertiserManagement/CreateEditPaidProject/CreateEditPaidProject";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const LcwNewPartners = () => {
    const { isToastOpen, toggleToast, toastContent, setToastContent } = useToast();
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [appliedFilters, setAppliedFilters] = useState({ startDate: null, endDate: null });

    const fetchUsers = useCallback(async (abortController, filters = {}) => {
        setIsLoading(true);
        setLoadingProgress(0);
        setTableData([]);

        // Accumulate users locally to avoid re-rendering on each user
        const accumulatedUsers = [];

        const options = {
            signal: abortController.signal,
            minFollowers: 500,
            maxFollowers: 10000,
        };

        // Add date filters if provided
        if (filters.startDate) {
            options.startDate = filters.startDate.format('YYYY-MM-DD');
        }
        if (filters.endDate) {
            options.endDate = filters.endDate.format('YYYY-MM-DD');
        }

        await getAllLCWUsersStream(
            // onUser callback - called for each user received
            (user) => {
                accumulatedUsers.push(user);
                setLoadingProgress(accumulatedUsers.length);
            },
            // onComplete callback
            () => {
                // Set all users at once when streaming is complete
                setTableData(accumulatedUsers);
                setIsLoading(false);
            },
            // onError callback
            (error) => {
                console.error('Stream error:', error);
                // Still show any users that were received before the error
                if (accumulatedUsers.length > 0) {
                    setTableData(accumulatedUsers);
                }
                setToastContent(t("common-server-err-msg"));
                toggleToast();
                setIsLoading(false);
            },
            options
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const abortController = new AbortController();
        fetchUsers(abortController, appliedFilters);

        // Cleanup: abort the request when component unmounts
        return () => {
            abortController.abort();
        };
    }, [fetchUsers, appliedFilters]);

    const handleApplyFilters = () => {
        setAppliedFilters({ startDate, endDate });
    };

    const handleClearFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setAppliedFilters({ startDate: null, endDate: null });
    };

    const lcwUsersColumns = {};
    lcwUsersColumns.visibility = {}
    const handleUpdateLCWUser = async (original) => {
        const { is_reviewed, user_id } = original;
        const response = await updateLCWUserReviewState({
            is_reviewed: !is_reviewed,
            user_id
        })

        // Update in main table data
        let selectedUser = tableData.find(user => user.user_id === user_id);
        if (!!selectedUser) {
            selectedUser.is_reviewed = !is_reviewed;
        }

        setToastContent(!!response.data.isUpdated ?
            t("lcw-new-users.is-reviewed-state-changed-success-msg") :
            t("common-server-err-msg"));
        toggleToast();
    };
    lcwUsersColumns.data = [
        {
            accessorKey: 'is_reviewed',
            header: "Is reviewed?",
            Cell: (cell) => (
                <div className={styles.updateCell}>
                    <Button
                        variant="contained"
                        size={"small"}
                        color={cell.row.original.is_reviewed ? "success" : "error"}
                        startIcon={cell.row.original.is_reviewed ? <DoneOutlined /> : <BlockOutlined />}
                        onClick={(e) => {
                            handleUpdateLCWUser(cell.row.original);
                        }}>
                        {cell.row.original.is_reviewed ? "YES" : "NO"}
                    </Button>
                </div>
            )
        },
        {
            accessorKey: 'instagram_account',
            header: "Instagram Account",
            Cell: (cell) => (
                <div>
                    {!!cell.row.original.instagram_account && <div className={styles.socialHolder}>
                        <div>{cell.row.original.instagram_account}</div>
                        <Button
                            variant="contained"
                            sx={{ mT: 1, width: "fit-content" }}
                            startIcon={<LaunchOutlined />}
                            size={"small"}
                            onClick={(e) => {
                                let username = getInstagramLink(cell.row.original.instagram_account);
                                username = (username[0] === "/") ?
                                    username.substring(1) :
                                    username;
                                window.open(`https://www.instagram.com/${username}`, '_blank');
                            }}>{t("common.open")}
                        </Button>
                    </div>}
                </div>
            )
        },
        {
            accessorKey: 'user_id',
            header: "User Id",
        },
        {
            accessorKey: 'firstname',
            header: "Name & Surname",
            Cell: (cell) => (
                <div>{cell.row.original.firstname} {cell.row.original.lastname}</div>
            )
        },
        {
            accessorKey: 'email',
            header: t("common.email"),
        },
        {
            accessorKey: 'phone',
            header: "Phone",
        },
        {
            accessorKey: 'follower_count',
            header: "Follower Count",
        },
        {
            accessorKey: 'tiktok_account',
            header: "Tiktok Account",
        },
        {
            accessorKey: 'x_account',
            header: "X Account",
        },
        {
            accessorKey: 'categories',
            header: "Categories",
            size: 600,
            Cell: (cell) => (
                <div>
                    {!!cell.row.original.categories &&
                        <div className={styles.tagsHolder}>
                            {JSON.parse(cell.row.original.categories).map(category => (
                                <div key={category}><Chip color="primary" sx={{ fontWeight: 700 }}
                                    label={category}></Chip></div>
                            ))}
                        </div>
                    }
                </div>
            )
        },
        {
            accessorKey: 'created_at',
            header: "Created At",
            Cell: (cell) => (
                <div>
                    {dayjs(cell.row.original.created_at, "YYYY-MM-DD+h:mm").format("YYYY-MM-DD h:mm")}
                </div>
            )
        }
    ];


    const handleDownloadFile = async (fileType) => {
        switch (fileType) {
            case EXPORT_FILE_TYPES.excel:
                window.open((process.env.REACT_APP_DASHBOARD_API ?? "http://localhost:4000") + DOWNLOAD_LCW_USERS_CSV + "?type=excel", "_blank");
                break;
            case EXPORT_FILE_TYPES.csv:
                window.open((process.env.REACT_APP_DASHBOARD_API ?? "http://localhost:4000") + DOWNLOAD_LCW_USERS_CSV + "?type=csv", "_blank");
                break;
            default:
                break;

        }
    }

    return (
        <div className={styles.container}>
            <BreadcrumbsBar />

            {/* Main LCW Nano Partners Card */}
            <Card title="Lcw Nano Partners" customIcon={<LocalDiningOutlined />}>
                {/* Filters and Export Container */}
                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 2
                        }}
                    >
                        {/* Date Filters Section */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <DatePicker
                                        label="Start Date"
                                        value={startDate}
                                        onChange={(newValue) => setStartDate(newValue)}
                                        slotProps={{ textField: { size: 'small' } }}
                                    />
                                    <DatePicker
                                        label="End Date"
                                        value={endDate}
                                        onChange={(newValue) => setEndDate(newValue)}
                                        minDate={startDate}
                                        slotProps={{ textField: { size: 'small' } }}
                                    />
                                </Stack>
                            </LocalizationProvider>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<FilterAlt />}
                                    onClick={handleApplyFilters}
                                    disabled={isLoading}
                                >
                                    Apply
                                </Button>
                                {(appliedFilters.startDate || appliedFilters.endDate) && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<Clear />}
                                        onClick={handleClearFilters}
                                        disabled={isLoading}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Stack>
                        </Box>

                        {/* Export Section */}
                        <ExportDropdown handleCb={handleDownloadFile} />
                    </Box>
                </Box>

                {isLoading && (
                    <Box sx={{ width: '100%', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Loading users... ({loadingProgress} loaded)
                            </Typography>
                        </Box>
                        <LinearProgress />
                    </Box>
                )}
                {tableData.length > 0 &&
                    <GenericTable
                        outputColumns={lcwUsersColumns}
                        tableData={tableData}
                        allRowsAreExpanded={tableData.length === 1}
                        rowCount={tableData.length}
                        enablePagination={true}
                    />
                }
            </Card>
            <Toast
                isOpen={isToastOpen}
                onClose={toggleToast}
                content={toastContent}
                severity="info"
            />
        </div>
    );
};

export default LcwNewPartners;
